import React from "react";
import { defineMessages, FormattedMessage as T, useIntl } from "react-intl";
import { makeStyles, Theme } from "@material-ui/core/styles";
import IconButton, { IconButtonProps } from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import RefreshIcon from "@material-ui/icons/Refresh";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import Skeleton from "@material-ui/lab/Skeleton";
import clsx from "clsx";
import moment from "moment";

import { Ticket } from "@egis-smartcity/parking";

const useStyles = makeStyles<Theme, Pick<TicketPanelProps, "invalidColor">>(
  (theme: Theme) => ({
    /* Styles applied to the root element. */
    root: {
      width: "100%",
      height: "100%",
    },
    /* Styles applied to the header container. */
    headerContainer: {
      height: 48,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      padding: 8,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      borderRadius: theme.shape.borderRadius,
    },
    title: {
      marginRight: 8,
    },
    spacer: {
      flex: 1,
    },
    contentContainer: {
      height: "calc(100% - 48px)",
      padding: 8,
      overflow: "auto",
    },
    ticketElement: {
      display: "flex",
      flexDirection: "column",
      marginBottom: 4,
      color: theme.palette.primary.dark,
      "& >:last-child": {
        display: "flex",
        alignItems: "center",
        backgroundColor: theme.palette.grey[300],
        padding: "8px 16px",
        "& >:nth-child(2)": {
          flex: 2,
          color: theme.palette.grey[800],
        },
        "& >:last-child": {
          textAlign: "end",
        },
        "& p": {
          flex: 1,
        },
      },
      "& >*": {
        display: "flex",
        justifyContent: "space-between",
      },
      "& b": {
        color: "orange",
      },
    },
    headerElement: {
      backgroundColor: "#59a959",
      color: "white",
      padding: "2px 8px",
      textTransform: "uppercase",
    },
    dateElement: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      "& >:nth-child(2)": {
        margin: "0px 8px",
      },
    },
    dateFormat: {
      "& >*": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        fontSize: 12,
      },
    },
    updateButton: {
      backgroundColor: "white",
      color: theme.palette.primary.main,
      borderRadius: 4,
      height: 32,
      width: 32,
      "&:hover": {
        backgroundColor: theme.palette.grey[300],
        transition: "all 150ms ease-in-out",
      },
    },
    /* Styles applied to the root element if `valid={false}` and the component is not loading. */
    invalid: {
      backgroundColor: ({ invalidColor }) => {
        return invalidColor || theme.palette.error.light;
      },
    },
    invalidFont: {
      color: ({ invalidColor }) => {
        return invalidColor || theme.palette.error.light;
      },
    },
  })
);

const msg = defineMessages({
  title: {
    id: "ticketControl.title",
    description: "Title of the TicketControl component.",
    defaultMessage: "Ticket",
  },
  refreshButtonTooltipLabel: {
    id: "ticketControl.refreshButtonTooltip.label",
    description: "Label of the refresh button tooltip.",
    defaultMessage: "Refresh",
  },
  free: {
    id: "ticketControl.free",
    description: "Label of the ticket when free.",
    defaultMessage: "Free",
  },
  sector: {
    id: "ticketControl.sector",
    description: "Label of the ticket sector.",
    defaultMessage: "Sector",
  },
  daySufix: {
    id: "ticketControl.daySufix",
    description: "Sufix to the days remaining.",
    defaultMessage: "D",
  },
  noTicketAvailable: {
    id: "ticketControl.noTicketAvailable",
    description: "Message displayed when no ticket is available.",
    defaultMessage: "No ticket available.",
  },
});

export type TicketPanelProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  /**
   * Tickets.
   */
  tickets?: Ticket[];
  /**
   * Control date.
   */
  controlDate?: string;
  /**
   * Tickets validity.
   */
  valid?: boolean;
  /**
   * Color to highlight the ticket invalidity.
   * (default to `theme.palette.error.light`)
   */
  invalidColor?: string;
  onRefreshButtonClick?: IconButtonProps["onClick"];
};

function TicketPanel(props: TicketPanelProps) {
  const {
    className,
    controlDate,
    tickets,
    valid,
    invalidColor,
    onRefreshButtonClick,
    ...other
  } = props;
  const { formatMessage: t } = useIntl();

  const classes = useStyles({ invalidColor });

  const loading = React.useMemo(() => {
    return valid === undefined;
  }, [valid]);

  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  const getValidity = (end: string) => {
    return moment(end).diff(moment(controlDate)) > 0
      ? moment(end).diff(moment(controlDate))
      : 0;
  };

  return (
    <div className={rootClasses} {...other}>
      <div className={classes.headerContainer}>
        <Typography className={classes.title} variant="h6" noWrap>
          <T {...msg.title} />
        </Typography>
        <div className={classes.spacer} />
        <Tooltip title={t(msg.refreshButtonTooltipLabel)}>
          <span>
            <IconButton
              className={classes.updateButton}
              aria-label={t(msg.refreshButtonTooltipLabel)}
              color="inherit"
              disabled={loading}
              onClick={onRefreshButtonClick}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </div>
      <div className={classes.contentContainer}>
        {tickets === undefined ? (
          <Skeleton variant="rect" height="100%" />
        ) : (
          <>
            {tickets.length === 0 ? (
              <Alert severity="warning">
                <T {...msg.noTicketAvailable} />
              </Alert>
            ) : (
              tickets.map((ticket, index) => (
                <div className={classes.ticketElement} key={index}>
                  <div
                    className={
                      getValidity(ticket.endDateTime)
                        ? classes.headerElement
                        : clsx(classes.headerElement, classes.invalid)
                    }
                  >
                    <Typography>
                      {ticket.amountPaid === 0 ? (
                        <T {...msg.free} />
                      ) : (
                        <>
                          {ticket.amountPaid / 100} {ticket.amountCurrency}
                        </>
                      )}{" "}
                      - {ticket.profile}
                    </Typography>
                  </div>
                  <div>
                    <Typography>
                      <T {...msg.sector} />:{" "}
                      <b>
                        {ticket.tariffCode
                          ? ticket.tariffCode
                          : ticket.locationCode}
                      </b>
                    </Typography>
                    <div className={classes.dateElement}>
                      <div className={classes.dateFormat}>
                        <Typography>
                          {moment(ticket.startDateTime).format("L")}
                        </Typography>
                        <Typography>
                          {moment(ticket.startDateTime).format("LTS")}
                        </Typography>
                      </div>
                      <ChevronRightIcon color="primary" />
                      <div className={classes.dateFormat}>
                        <Typography>
                          {moment(ticket.endDateTime).format("L")}
                        </Typography>
                        <Typography>
                          {moment(ticket.endDateTime).format("LTS")}
                        </Typography>
                      </div>
                    </div>
                    <Typography
                      className={
                        getValidity(ticket.endDateTime)
                          ? undefined
                          : classes.invalidFont
                      }
                    >
                      {Math.floor(
                        moment
                          .duration(
                            getValidity(ticket.endDateTime),
                            "milliseconds"
                          )
                          .asDays()
                      )}{" "}
                      <T {...msg.daySufix} />
                    </Typography>
                  </div>
                </div>
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TicketPanel;
